import {
    Chart,
    ChartConfiguration,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getHoursAndMinutes } from '../../../../utils/time';

// Register components once
Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    LineController,
    PointElement,
    Title,
    Tooltip,
    ChartDataLabels,
);

/**
 * 共通縦棒グラフ（平均線 + オプション前期ライン）
 */
export const createVerticalBarChart = (
    ctx: CanvasRenderingContext2D | null,
    // 今期用ラベル
    labels: string[],
    // 今期データ
    currentValues: number[],
    // 前期用ラベル(任意)
    previousLabels?: string[],
    // 前期データ(任意)
    previousValues?: number[],
    options?: {
        averageAllLabel?: string;
        averageActiveLabel?: string;
        previousLabel?: string;
        formatDuration?: (minutes: number) => string;
        formatShortDuration?: (minutes: number) => string;
    },
): Chart<'bar' | 'line'> | undefined => {
    if (!ctx) return undefined;

    // -------- 平均値計算 --------
    const total = currentValues.reduce((s, v) => s + v, 0);
    const periodDays = currentValues.length;
    const activeDays = currentValues.filter((v) => v > 0).length;
    const avgPeriod = periodDays ? total / periodDays : 0;
    const avgActive = activeDays ? total / activeDays : 0;

    const averageAllLabel = options?.averageAllLabel ?? '平均(全日)';
    const averageActiveLabel = options?.averageActiveLabel ?? '平均(活動日)';
    const previousLabel = options?.previousLabel ?? '前期';
    const formatDuration = options?.formatDuration ?? defaultFormatDuration;
    const formatShortDuration = options?.formatShortDuration ?? defaultFormatShortDuration;

    // -------- データセット構築 --------
    const datasets: any[] = [
        // 平均(全日) 実線
        {
            type: 'line',
            label: averageAllLabel,
            borderColor: '#e3e3e3',
            borderWidth: 1.5,
            backgroundColor: 'transparent',
            pointRadius: 0,
            pointHitRadius: 12,
            clip: false,
            data: Array(labels.length).fill(avgPeriod),
            order: 3,
            datalabels: { display: false },
        },
        // 平均(活動日) 破線
        {
            type: 'line',
            label: averageActiveLabel,
            borderColor: '#d6d6d6',
            borderDash: [4, 4],
            borderWidth: 1.5,
            backgroundColor: 'transparent',
            pointRadius: 0,
            pointHitRadius: 12,
            clip: false,
            data: Array(labels.length).fill(avgActive),
            order: 3,
            datalabels: { display: false },
        },
    ];

    // 前期ラインがあれば追加（灰色）
    if (previousValues && previousValues.length) {
        datasets.push({
            type: 'line',
            label: previousLabel,
            borderColor: '#c4c4c4',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointBackgroundColor: '#c4c4c4',
            pointBorderColor: '#c4c4c4',
            pointRadius: 4,
            pointHitRadius: 12,
            fill: false,
            data: previousValues,
            order: 1,
            datalabels: { display: false },
        });
    }

    // 今期バー
    datasets.push({
        type: 'bar',
        label: '',
        backgroundColor: '#FFD700',
        data: currentValues,
        order: 2,
        datalabels: {
            display: (ctx: any) => {
                const value = ctx?.dataset?.data?.[ctx.dataIndex];
                return typeof value === 'number' && value > 0;
            },
            align: 'center',
            anchor: 'center',
            font: { size: 13, lineHeight: 0.9 },
            color: '#919191',
            formatter: createDataLabelsFormatter(currentValues, formatShortDuration),
            textAlign: 'center',
        },
    });

    // -------- チャート設定 --------
    const config: ChartConfiguration<'bar' | 'line'> = {
        type: 'bar',
        data: {
            labels,
            datasets,
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'nearest', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        // ツールチップのラベル
                        label: (ctx) => {
                            const dsLabel = ctx.dataset.label as string;
                            const rawVal = Number(ctx.parsed.y);
                            const val = Math.round(rawVal);
                            const timeText = formatDuration(val);
                            if (dsLabel === averageAllLabel)
                                return `${averageAllLabel}\n${timeText}`;
                            if (dsLabel === averageActiveLabel)
                                return `${averageActiveLabel}\n${timeText}`;
                            return timeText;
                        },
                        // ツールチップのタイトル
                        title: (ctx) => {
                            const idx = ctx[0].dataIndex;
                            const dsLabel = ctx[0].dataset.label as string;
                            // 平均線はタイトル非表示
                            if (dsLabel === averageAllLabel || dsLabel === averageActiveLabel)
                                return '';
                            // 前期ラベルの表示
                            if (dsLabel === previousLabel && previousValues) {
                                return previousLabels?.[idx] ?? '';
                            }
                            return labels[idx];
                        },
                    },
                },
            },
            scales: {
                x: { ticks: { maxTicksLimit: 8, maxRotation: 0, minRotation: 0 } },
                y: {
                    ticks: {
                        maxTicksLimit: 8,
                        callback: (v) => formatShortDuration(Number(v)),
                    },
                },
            },
        },
    };

    return new Chart(ctx, config);
};

// ------------- util --------------
function createDataLabelsFormatter(
    data: (number | string)[],
    formatShortDuration: (minutes: number) => string,
) {
    return (_value: any, ctx: any): string | undefined => {
        const index = ctx.dataIndex;
        if (index === undefined || index >= data.length) return undefined;
        const val = data[index];
        if (typeof val !== 'number' || val === 0) return undefined;
        const text = formatShortDuration(val);
        return Array.from(text).join('\n');
    };
}

function defaultFormatDuration(minutes: number): string {
    const [h, m] = getHoursAndMinutes(minutes);
    if (h === 0) return `${m}分`;
    return `${h}時間${m}分`;
}

function defaultFormatShortDuration(minutes: number): string {
    const [h, m] = getHoursAndMinutes(minutes);
    if (h === 0) return `${m}m`;
    return `${h}h${m}m`;
}
